import React, { Component } from 'react';
import PathColorIcon from '@mui/icons-material/BorderColor';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { SketchPicker } from 'react-color';

import { PathAnnotation, StrokeStyle } from '@paragon/contract-library';

import AnnotationOptionsSection, { AnnotationsOptionsRow } from './AnnotationOptionsSection';
import FloatInput from './FloatInput';

interface Props {
  readonly annotationBeingEdited: PathAnnotation;
}

interface State {
  readonly strokeWidth: number;
  readonly strokeStyle: StrokeStyle;
  readonly color: string;
  readonly displayAdvancedColorPicker: boolean;
  readonly restrictOrthogonal: boolean;
  readonly firstPointX: number;
  readonly firstPointY: number;
  readonly lastPointX: number;
  readonly lastPointY: number;
}

export default class PathOptions extends Component<Props, State> {
  advancedColorButtonRef: React.RefObject<HTMLButtonElement | null> = React.createRef();

  state: State = {
    strokeWidth: this.props.annotationBeingEdited.strokeWidth ? this.props.annotationBeingEdited.strokeWidth : 1.0,
    strokeStyle: this.props.annotationBeingEdited.strokeStyle ? this.props.annotationBeingEdited.strokeStyle : 'solid',
    color: this.props.annotationBeingEdited.color,
    displayAdvancedColorPicker: false,
    restrictOrthogonal: this.props.annotationBeingEdited.restrictOrthogonal
      ? this.props.annotationBeingEdited.restrictOrthogonal
      : false,

    firstPointX: this.props.annotationBeingEdited.segments[0].x,
    firstPointY: this.props.annotationBeingEdited.segments[0].y,

    lastPointX: this.props.annotationBeingEdited.segments[this.props.annotationBeingEdited.segments.length - 1].x,
    lastPointY: this.props.annotationBeingEdited.segments[this.props.annotationBeingEdited.segments.length - 1].y,
  };

  onStrokeStyleChange = (event: SelectChangeEvent<StrokeStyle>) => {
    this.setState({
      strokeStyle: event.target.value as StrokeStyle,
    });
  };

  onStrokeWidthChange = (strokeWidth: number) => {
    this.setState({ strokeWidth });
  };

  onRestrictOrthogonalChange = () => {
    if (!this.state.restrictOrthogonal) {
      this.applyOrthogonalRestriction(0, true);
    }

    this.setState((prevState) => ({ restrictOrthogonal: !prevState.restrictOrthogonal }));
  };

  onFirstPointXChange = (value: number) => {
    this.setState({ firstPointX: value }, () => this.applyOrthogonalRestriction(0));
  };

  onFirstPointYChange = (value: number) => {
    this.setState({ firstPointY: value }, () => this.applyOrthogonalRestriction(0));
  };

  onLastPointXChange = (value: number) => {
    this.setState({ lastPointX: value }, () => this.applyOrthogonalRestriction(1));
  };

  onLastPointYChange = (value: number) => {
    this.setState({ lastPointY: value }, () => this.applyOrthogonalRestriction(1));
  };

  get updatedProperties() {
    return {
      ...this.state,

      segments: [
        {
          x: this.state.firstPointX,
          y: this.state.firstPointY,
        },
        {
          x: this.state.lastPointX,
          y: this.state.lastPointY,
        },
      ],
    };
  }

  applyOrthogonalRestriction = (pointChanged: number, force = false) => {
    if (!force && !this.state.restrictOrthogonal) {
      return;
    }

    const { segments: originalSegments } = this.props.annotationBeingEdited;
    const { segments: newSegments } = this.updatedProperties;

    const xChange = Math.abs(originalSegments[1].x - originalSegments[0].x);
    const yChange = Math.abs(originalSegments[1].y - originalSegments[0].y);
    const wasOriginallyMoreVertical = xChange < yChange;

    const restrictedDimension = wasOriginallyMoreVertical ? 'x' : 'y';
    const [thisPoint, otherPoint] = pointChanged === 0 ? ['first', 'last'] : ['last', 'first'];

    if (newSegments[0][restrictedDimension] !== newSegments[1][restrictedDimension]) {
      const dimension = restrictedDimension.toUpperCase();

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.setState((prevState) => ({
        [`${otherPoint}Point${dimension}`]: prevState[`${thisPoint}Point${dimension}` as keyof State],
      }));
    }
  };

  render() {
    return (
      <AnnotationOptionsSection title="Path" titleMargin={22}>
        <AnnotationsOptionsRow>
          <FloatInput label="Line weight" value={this.state.strokeWidth} onChange={this.onStrokeWidthChange} />

          <FormControl>
            <InputLabel htmlFor="stroke-style">Style</InputLabel>
            <Select
              id="strokeStyle"
              value={this.state.strokeStyle}
              inputProps={{ id: 'stroke-style' }}
              onChange={this.onStrokeStyleChange}
              label="Style"
            >
              <MenuItem id="strokeStyleSolid" value="solid">
                Solid
              </MenuItem>
              <MenuItem id="strokeStyleDashed" value="dashed">
                Dashed
              </MenuItem>
            </Select>
          </FormControl>

          <IconButton
            ref={this.advancedColorButtonRef}
            onClick={() => this.setState({ displayAdvancedColorPicker: true })}
          >
            <PathColorIcon sx={{ color: this.state.color }} />
          </IconButton>

          <Popover
            anchorEl={this.advancedColorButtonRef.current}
            open={this.state.displayAdvancedColorPicker}
            onClose={() => void this.setState({ displayAdvancedColorPicker: false })}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <SketchPicker
              color={this.state.color}
              onChangeComplete={(color) =>
                void this.setState({
                  color: color.hex,
                })
              }
              disableAlpha
            />
          </Popover>
        </AnnotationsOptionsRow>

        <AnnotationsOptionsRow>
          <FloatInput label="First Point X" value={this.state.firstPointX} onChange={this.onFirstPointXChange} />

          <FloatInput label="First Point Y" value={this.state.firstPointY} onChange={this.onFirstPointYChange} />
        </AnnotationsOptionsRow>

        <AnnotationsOptionsRow>
          <FloatInput label="Second Point X" value={this.state.lastPointX} onChange={this.onLastPointXChange} />

          <FloatInput label="Second Point Y" value={this.state.lastPointY} onChange={this.onLastPointYChange} />
        </AnnotationsOptionsRow>

        <AnnotationsOptionsRow>
          <FormControlLabel
            label="Restrict Orthogonal"
            control={
              <Checkbox
                color="default"
                checked={this.state.restrictOrthogonal}
                onChange={this.onRestrictOrthogonalChange}
              />
            }
          />
        </AnnotationsOptionsRow>
      </AnnotationOptionsSection>
    );
  }
}
